// Sticky banner shown at the top of the chat while a voice-training session is
// active. Presentational: VoiceTrainingButton owns the audio graph and session
// state and renders this via a portal into a slot in chat.tsx. The waveform
// reads whichever analyser belongs to the side currently speaking (the mic
// goes flat while the assistant speaks — echo-cancelled out of the capture).

import { Mic, MicOff, Square } from "lucide-react";
import { Button } from "../ui/button";
import { WaveformVisualizer } from "./WaveformVisualizer";

const MUTE_LABEL = "Silenciar microfono";
const UNMUTE_LABEL = "Activar microfono";
const STOP_LABEL = "Detener entrenamiento por voz";

interface VoiceSessionBannerProps {
  statusLabel: string;
  /** Pulses the status label while the turn is mid-flight (processing/searching). */
  busy?: boolean;
  elapsedSeconds: number;
  micAnalyser: AnalyserNode | null;
  outputAnalyser: AnalyserNode | null;
  speaking: "user" | "assistant";
  muted: boolean;
  onToggleMute: () => void;
  onStop: () => void;
  /** Current TTS playback speed (sandwich); shown on the cycle control. */
  ttsSpeed?: number;
  /** Advances the TTS speed to the next step. Omitted hides the control. */
  onCycleSpeed?: () => void;
  /** Seconds left before idle auto-stop, or null when no warning is showing. */
  idleSeconds: number | null;
  onKeepAlive: () => void;
}

function formatElapsed(totalSeconds: number): string {
  const minutes = Math.floor(totalSeconds / 60);
  const seconds = totalSeconds % 60;
  return `${minutes}:${seconds.toString().padStart(2, "0")}`;
}

export function VoiceSessionBanner({
  statusLabel,
  busy = false,
  elapsedSeconds,
  micAnalyser,
  outputAnalyser,
  speaking,
  muted,
  onToggleMute,
  onStop,
  ttsSpeed = 1,
  onCycleSpeed,
  idleSeconds,
  onKeepAlive,
}: VoiceSessionBannerProps) {
  const muteLabel = muted ? UNMUTE_LABEL : MUTE_LABEL;
  const MuteIcon = muted ? MicOff : Mic;
  const activeAnalyser = speaking === "assistant" ? outputAnalyser : micAnalyser;
  const showIdleWarning = idleSeconds !== null;

  return (
    <div className="mx-auto flex max-w-3xl items-center gap-3 rounded-lg border border-primary bg-background px-3 py-2 shadow-sm">
      <span className="relative flex h-2.5 w-2.5 shrink-0" aria-hidden="true">
        <span className="absolute inline-flex h-full w-full animate-ping rounded-full bg-primary opacity-75" />
        <span className="relative inline-flex h-2.5 w-2.5 rounded-full bg-primary" />
      </span>

      {showIdleWarning ? (
        <output className="flex flex-1 items-center gap-2 text-body-3 text-amber-600">
          <span>Sin actividad — se cerrará en {idleSeconds}s</span>
          <Button
            type="button"
            variant="outline"
            size="sm"
            className="!h-7 !px-2"
            onClick={onKeepAlive}
          >
            Seguir
          </Button>
        </output>
      ) : (
        <>
          <span
            className={`shrink-0 text-body-3 font-medium text-foreground${busy ? " animate-pulse" : ""}`}
          >
            {statusLabel}
          </span>
          {/* min-w-0 + overflow-hidden: the canvas has a fixed pixel width, so
              on narrow screens it clips here instead of pushing the mute/stop
              buttons out of the viewport. */}
          <div className="flex min-w-0 flex-1 justify-center overflow-hidden text-primary">
            <WaveformVisualizer
              {...(activeAnalyser ? { externalAnalyser: activeAnalyser } : {})}
              barCount={32}
              height={32}
              className="block"
            />
          </div>
          <span className="shrink-0 font-mono text-body-3 text-muted-foreground tabular-nums">
            {formatElapsed(elapsedSeconds)}
          </span>
        </>
      )}

      {onCycleSpeed ? (
        <Button
          type="button"
          variant="ghost"
          size="sm"
          className="!h-7 !px-1.5 shrink-0 font-mono text-body-3 tabular-nums"
          onClick={onCycleSpeed}
          aria-label={`Velocidad de la voz: ${ttsSpeed}x`}
          title="Velocidad de la voz"
        >
          {ttsSpeed}×
        </Button>
      ) : null}
      <Button
        type="button"
        variant="ghost"
        size="sm"
        className={`!h-7 !px-1.5 shrink-0 ${muted ? "text-destructive" : ""}`}
        onClick={onToggleMute}
        aria-label={muteLabel}
        aria-pressed={muted}
        title={muteLabel}
      >
        <MuteIcon className="h-3.5 w-3.5" />
      </Button>
      <Button
        type="button"
        variant="ghost"
        size="sm"
        className="!h-7 !px-1.5 shrink-0 text-destructive"
        onClick={onStop}
        aria-label={STOP_LABEL}
        title={STOP_LABEL}
      >
        <Square className="h-3.5 w-3.5 fill-current" />
      </Button>
    </div>
  );
}
